// Calendar-date helpers. 'YYYY-MM-DD' strings are civil dates in the household
// timezone; arithmetic on them is done in UTC to avoid DST surprises.

export type IsoDate = string;

export function parseIsoDate(d: IsoDate): { y: number; m: number; day: number } {
  const [y, m, day] = d.split("-").map(Number);
  return { y: y!, m: m!, day: day! };
}

export function toIsoDate(y: number, m: number, day: number): IsoDate {
  const dt = new Date(Date.UTC(y, m - 1, day));
  return dt.toISOString().slice(0, 10);
}

export function addDays(d: IsoDate, n: number): IsoDate {
  const { y, m, day } = parseIsoDate(d);
  return toIsoDate(y, m, day + n);
}

/** Add months, clamping to the last day of the target month (Jan 31 + 1 = Feb 28/29). */
export function addMonths(d: IsoDate, n: number): IsoDate {
  const { y, m, day } = parseIsoDate(d);
  const target = new Date(Date.UTC(y, m - 1 + n, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  return toIsoDate(target.getUTCFullYear(), target.getUTCMonth() + 1, Math.min(day, lastDay));
}

export function weekday(d: IsoDate): number {
  const { y, m, day } = parseIsoDate(d);
  return new Date(Date.UTC(y, m - 1, day)).getUTCDay();
}

export function compareIsoDate(a: IsoDate, b: IsoDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Today's civil date in an IANA timezone. */
export function todayIn(timeZone: string, now: Date = new Date()): IsoDate {
  return zonedParts(now, timeZone).date;
}

// Building an Intl.DateTimeFormat is far slower than using one; keep one per zone.
const zonedFormats = new Map<string, Intl.DateTimeFormat>();
function zonedFormat(timeZone: string): Intl.DateTimeFormat {
  let f = zonedFormats.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
    zonedFormats.set(timeZone, f);
  }
  return f;
}

/** Civil date and minutes-after-midnight of an instant in a timezone. */
export function zonedParts(instant: Date, timeZone: string): { date: IsoDate; minutes: number } {
  const parts = zonedFormat(timeZone).formatToParts(instant);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    minutes: Number(get("hour")) * 60 + Number(get("minute")),
  };
}

/** UTC offset (minutes) of a timezone at a given instant. */
export function zoneOffsetMinutes(instant: Date, timeZone: string): number {
  const { date, minutes } = zonedParts(instant, timeZone);
  const { y, m, day } = parseIsoDate(date);
  const asUtc = Date.UTC(y, m - 1, day, Math.floor(minutes / 60), minutes % 60);
  const truncated = Math.floor(instant.getTime() / 60_000) * 60_000;
  return Math.round((asUtc - truncated) / 60_000);
}

/** The instant at which a civil date begins (local midnight) in a timezone. DST-safe. */
export function startOfDayInstant(d: IsoDate, timeZone: string): Date {
  const { y, m, day } = parseIsoDate(d);
  const guess = Date.UTC(y, m - 1, day);
  // Two passes settle the offset even when midnight is near a DST transition.
  let ts = guess - zoneOffsetMinutes(new Date(guess), timeZone) * 60_000;
  ts = guess - zoneOffsetMinutes(new Date(ts), timeZone) * 60_000;
  return new Date(ts);
}
